import { Typography, useMediaQuery, useTheme } from '@material-ui/core'

import { getTranslate, lang } from '../../localization'
import { AddOnPolicy } from '../../types/cancelAndModifyPolicies'
import { AddOn } from '../../types/rateplan'
import InventoryInputItem from './InventoryInputItem'
import {
  PolicyChoice,
  REMOVE_ADD_ON_POLICY,
  removedAddOnCodes
} from './addOnPolicyAssignments'
import useStyles from './styles/AddonForBulkUpdate.style'

interface AddOnPoliciesForBulkUpdateProps {
  providerAddOns: AddOn[]
  providerName: string
  policies: AddOnPolicy[] | undefined
  loading: boolean
  choices: Record<string, PolicyChoice>
  setChoices: React.Dispatch<React.SetStateAction<Record<string, PolicyChoice>>>
}

// One policy picker per add-on, laid out like the rate row above it. Each picker is the
// cancel-policy picker (InventoryInputItem in its CRS policy mode) fed the provider's add-on
// sets plus a "remove" option; clearing a picker leaves that add-on's sets unchanged.
export default function AddOnPoliciesForBulkUpdate({
  providerAddOns,
  providerName,
  policies,
  loading,
  choices,
  setChoices
}: AddOnPoliciesForBulkUpdateProps) {
  const classes = useStyles()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  // An add-on without a translated name falls back to its own name rather than throwing.
  const nameOf = (addOn: AddOn) =>
    lang === 'en'
      ? addOn.name
      : addOn.nameTranslatedList[0]?.value || addOn.name

  // Removal clears the set on every selected rate plan over every period, and bulk update has
  // no confirm step, so say which add-ons it applies to before Confirm.
  const removed = removedAddOnCodes(choices)
  const removedNames = providerAddOns
    .filter((addOn) => removed.includes(addOn.code))
    .map(nameOf)

  const options = {
    cancelPolicies: [
      ...(policies ?? []),
      {
        basicDetails: {
          id: REMOVE_ADD_ON_POLICY,
          name: getTranslate('Remove Policy'),
          description: '',
          descriptionTranslatedList: []
        },
        table: []
      }
    ]
  }

  return (
    <div className={classes.root}>
      <div className={classes.restrictionSelector}>
        <div className={classes.titleRestrictions}>
          <Typography className={classes.ratePlaneTextAddons}>
            {providerName}
          </Typography>
        </div>
        <div
          className={[
            classes.valueContainer,
            classes.restrictionSelectorTitle
          ].join(' ')}>
          <Typography>
            {isSmall ? '' : getTranslate('Policy') + ' :'}
          </Typography>
        </div>
      </div>
      {providerAddOns.map((item) => (
        <InventoryInputItem
          key={item.code}
          label={nameOf(item)}
          isChannelSelected="CRS"
          value={choices[item.code]?.value}
          cancelPolicyData={options}
          cancelPolicyValue={choices[item.code] ?? null}
          setCancelPolicyValue={(choice: PolicyChoice) =>
            setChoices((current) => ({ ...current, [item.code]: choice }))
          }
          loadingForCancelPolicy={loading}
        />
      ))}
      {removedNames.length > 0 && (
        <Typography
          variant="caption"
          color="error"
          role="alert"
          className={classes.removalWarning}>
          {getTranslate(
            'Confirm will remove the policy of these add-ons on every selected rate plan for the selected dates:'
          )}{' '}
          {removedNames.join(lang === 'en' ? ', ' : '، ')}
        </Typography>
      )}
    </div>
  )
}
